import {
  FlexColumn,
  FlexRow,
  MaterialIcon,
  StyledBanner,
  StyledButton,
  useLoading,
  useModal,
} from '@gorila-shared-ui/components';
import { Banner } from 'baseui/banner';
import { Button } from 'baseui/button';
import { OnChangeParams } from 'baseui/select';
import { Textarea } from 'baseui/textarea';
import { HeadingSmall, LabelSmall, ParagraphMedium, ParagraphXSmall } from 'baseui/typography';
import { useEffect, useMemo, useState } from 'react';
import { areEqual } from 'react-window';
import { useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';
import { FEEDBACK, FEEDBACK_PREFIXES } from '../../constants/app';
import {
  JOURNEY_END_POINT,
  JOURNEY_START_POINT,
  JOURNEY_STATUS_ACTIVE,
  JOURNEY_STATUS_FINISHED,
  NO_COMMUNICATION_TIME_DEFAULT,
  NO_COMMUNICATION_TIME_OPTIONS,
} from '../../constants/journeys';
import { useFeedback } from '../../hooks/useFeedback';
import { useStyles } from '../../hooks/useStyles';
import { deleteJourney, getTravel, postJourney, updateJourney } from '../../services/journeyService';
import { apiParamsState } from '../../storage/app';
import {
  editingJourneyState,
  journeySelectPointState,
  selectedJourneyIdState,
  selectedJourneyState,
  updateJourneysState,
} from '../../storage/journeys';
import { Asset } from '../../types/asset';
import { GeoPlace } from '../../types/geoModels';
import { Journey, JourneySelectPoint, TravelElement } from '../../types/journey';
import { roundToClosestIntervalTime, roundUpToNextIntervalTime, timePlusMinutesFromSeconds } from '../../utils/dates';
import { getJourneyStatus } from '../../utils/journeys';
import { isGeoPlace } from '../../utils/map';
import { FloatingCardContent } from '../layout/cards/FloatingCardContent';
import { AssetPicker } from '../shared/AssetPicker';
import ConfirmModal from '../shared/ConfirmModal';
import { MapSearchPicker } from '../shared/MapSearchPicker';
import { LabeledSelect } from '../ui/LabeledSelect';
import { StyledDateTimeRangePicker } from '../ui/StyledDateTimeRangePicker';
import { StyledTooltip } from '../ui/StyledTooltip';

export function JourneyDetail() {
  const { theme, css, classes } = useStyles();
  const { openModal, closeModal, showModal } = useModal();
  const { loading, startLoading, stopLoading } = useLoading();
  const [selectedJourneyId, setSelectedJourneyId] = useRecoilState(selectedJourneyIdState);
  const [editingJourney, setEditingJourney] = useRecoilState(editingJourneyState);
  const [journeySelectPoint, setJourneySelectPoint] = useRecoilState(journeySelectPointState);
  const resetJourneySelectPoint = useResetRecoilState(journeySelectPointState);
  const [datesError, setDatesError] = useState('');
  const [travelInfo, setTravelInfo] = useState<TravelElement>();
  const apiParams = useRecoilValue(apiParamsState);
  const journey = useRecoilValue(selectedJourneyState);
  const resetSelectedJourneyId = useResetRecoilState(selectedJourneyIdState);
  const resetEditingJourney = useResetRecoilState(editingJourneyState);
  const setUpdateJourneys = useSetRecoilState(updateJourneysState);
  const { showDeletionFeedback, showFailFeedback, showPositiveFeedback } = useFeedback();

  useEffect(() => {
    resetEditingJourney();
    if (selectedJourneyId === '') {
      const initialDate = roundUpToNextIntervalTime();
      setEditingJourney((prev) => ({ ...prev!, startDate: initialDate, endDate: initialDate }));
    }
    return () => {
      resetEditingJourney();
      setTravelInfo(undefined);
    };
  }, [selectedJourneyId]);

  useEffect(() => {
    const loadTravel = async () => {
      if (!editingJourney?.startPoint || !editingJourney.endPoint || !editingJourney.startDate) return;
      if (editingJourney.startDate < Date.now()) return;

      const { error, rows } = await getTravel(editingJourney);
      if (rows && rows[0].elements[0] && !error) {
        const info = rows[0].elements[0];
        setTravelInfo(info);
        setEditingJourney((prev) =>
          prev && prev.startDate
            ? {
                ...prev,
                endDate: timePlusMinutesFromSeconds(prev.startDate, info.duration.value),
              }
            : prev
        );
      } else {
        showFailFeedback(error || 'No se pudo obtener la información del viaje.');
      }
    };

    loadTravel();
  }, [editingJourney?.startPoint, editingJourney?.endPoint, editingJourney?.startDate]);

  const minEndDate = useMemo(() => {
    if (travelInfo && editingJourney?.startDate) {
      return timePlusMinutesFromSeconds(editingJourney.startDate, travelInfo.duration.value);
    }
  }, [travelInfo, editingJourney?.startDate]);

  if (editingJourney === undefined) return null;

  const validEndDate =
    !!editingJourney.endDate &&
    editingJourney.endDate > new Date().getTime() &&
    editingJourney.endDate >= (minEndDate ?? 0) &&
    editingJourney.startDate >= Date.now();
  const invalidEndDateError = (() => {
    if (!validEndDate) {
      if (editingJourney.startDate < Date.now()) {
        return 'La fecha de salida no puede ser menor a la fecha actual';
      }
      if (editingJourney.endDate < (minEndDate ?? 0)) {
        return 'La llegada debe ser mayor al inicio más la duración estimada';
      }
      return 'La fecha de llegada no puede ser menor a la fecha actual';
    }
  })();

  const canSubmit =
    validEndDate &&
    !!editingJourney.startDate &&
    !!editingJourney.startPoint &&
    !!editingJourney.endPoint &&
    !!editingJourney.maxTimeNoCommunication &&
    !!editingJourney.asset;
  const hasUpdates = !areEqual(journey || {}, editingJourney);

  const onDeleteJourney = async () => {
    if (!apiParams || selectedJourneyId === undefined) return;
    const { error } = await deleteJourney(apiParams.clientId, apiParams.subClientId, selectedJourneyId);
    if (!error) {
      showDeletionFeedback(FEEDBACK.deleted(FEEDBACK_PREFIXES.journey, journey?._id));
      setUpdateJourneys('update');
      resetSelectedJourneyId();
      closeModal();
    } else {
      showFailFeedback(error || FEEDBACK.failedDeletion(FEEDBACK_PREFIXES.journey, journey?._id));
    }
  };

  const onSaveJourney = async () => {
    if (!apiParams || selectedJourneyId === undefined) return;
    startLoading();
    const { id, error } = isNewJourney
      ? await postJourney(apiParams.clientId, apiParams.subClientId, editingJourney)
      : await updateJourney(apiParams.clientId, apiParams.subClientId, editingJourney);
    if (!error && id) {
      if (isNewJourney) setSelectedJourneyId(id);
      showPositiveFeedback(
        isNewJourney
          ? FEEDBACK.created(FEEDBACK_PREFIXES.journey)
          : FEEDBACK.edited(FEEDBACK_PREFIXES.journey, editingJourney._id)
      );
      setUpdateJourneys(id);
    } else {
      showFailFeedback(
        error || isNewJourney
          ? FEEDBACK.failedCreation(FEEDBACK_PREFIXES.journey)
          : FEEDBACK.failedEdition(FEEDBACK_PREFIXES.journey, journey?._id)
      );
    }
    stopLoading();
  };

  const onAssetChange = (asset: Asset) => {
    setEditingJourney((prev) => ({ ...prev!, asset }));
  };

  const onMaxTimeNoCommunicationChange = (params: OnChangeParams) => {
    if (params.option?.id) {
      setEditingJourney((prev) => ({
        ...prev!,
        maxTimeNoCommunication: +params.option?.id! || NO_COMMUNICATION_TIME_DEFAULT,
      }));
    }
  };

  const onInputChange = (value: string | boolean | number | null, field: keyof Journey) => {
    setEditingJourney((prev) => ({ ...prev!, [field]: value }));
  };

  const toggleSelectPoint = (point: JourneySelectPoint) => {
    setJourneySelectPoint((prev) => (prev === point ? undefined : point));
  };

  const initStartSelectPoint = () => {
    setJourneySelectPoint(JOURNEY_START_POINT);
  };

  const initEndSelectPoint = () => {
    setJourneySelectPoint(JOURNEY_END_POINT);
  };

  const onChangeStart = (place: GeoPlace) => {
    setEditingJourney((prev) => ({
      ...prev!,
      startPoint: place.point,
      originAddress: place.address,
    }));
    resetJourneySelectPoint();
  };

  const onChangeEnd = (place: GeoPlace) => {
    setEditingJourney((prev) => ({
      ...prev!,
      endPoint: place.point,
      destinationAddress: place.address,
    }));
    resetJourneySelectPoint();
  };

  const isNewJourney = editingJourney?._id === '';
  const status = journey ? getJourneyStatus(journey?.status) : undefined;
  const isActive = status?.type === JOURNEY_STATUS_ACTIVE;
  const isFinished = status?.type === JOURNEY_STATUS_FINISHED;
  const canUpdateStart = !isActive && !isFinished;
  const canUpdateEnd = !isFinished;
  const startGeoPlace = { point: editingJourney.startPoint, address: editingJourney.originAddress };
  const endGeoPlace = { point: editingJourney.endPoint, address: editingJourney.destinationAddress };
  const startPlace = isGeoPlace(startGeoPlace) ? startGeoPlace : undefined;
  const endPlace = isGeoPlace(endGeoPlace) ? endGeoPlace : undefined;
  const isSelectingStart = journeySelectPoint === JOURNEY_START_POINT;
  const isSelectingEnd = journeySelectPoint === JOURNEY_END_POINT;

  return (
    <FloatingCardContent
      flexible
      gap={theme.sizing.scale600}
    >
      <FlexRow
        gap={theme.sizing.scale200}
        classNames={classes.centeredStart}
      >
        {status && (
          <StyledTooltip content={status.name}>
            <MaterialIcon
              name={status.icon}
              size="compact"
              color={status.color}
            />
          </StyledTooltip>
        )}
        <HeadingSmall margin={0}>
          {isNewJourney ? 'Nuevo trayecto' : `Trayecto ${journey?._id} - ${journey?.distance || 0} km`}
        </HeadingSmall>
      </FlexRow>
      <FlexColumn classNames={css({ flex: 1 })}>
        {isActive && (
          <Banner
            kind={'info'}
            hierarchy="low"
            overrides={{
              Root: {
                style: {
                  marginTop: 0,
                  marginRight: 0,
                  marginBottom: 0,
                  marginLeft: 0,
                },
              },
            }}
          >
            Los campos activo, fecha inicio y punto de partida no se podrán editar en trayectos que se encuentren
            activos.
          </Banner>
        )}
        <AssetPicker
          assetId={editingJourney.asset?._id}
          onChange={onAssetChange}
          maxWidth={300}
          disabled={isActive || isFinished}
          required
        />
        <LabeledSelect
          label="Tiempo máximo sin comunicar (mins):"
          options={NO_COMMUNICATION_TIME_OPTIONS}
          onChange={onMaxTimeNoCommunicationChange}
          value={[{ id: editingJourney.maxTimeNoCommunication }]}
          maxWidth={270}
          selectMaxWidth={80}
          disabled={isFinished}
          required
        />
        <FlexColumn gap={theme.sizing.scale200}>
          <LabelSmall>Punto de partida: *</LabelSmall>
          <FlexRow gap={theme.sizing.scale200}>
            {canUpdateStart && (
              <MapSearchPicker
                selectedPlace={startPlace}
                onChange={onChangeStart}
                onFocus={initStartSelectPoint}
                placeholder="Ingresa un punto de partida"
              />
            )}
            {!canUpdateStart && (
              <Textarea
                size="compact"
                value={editingJourney.originAddress || ''}
                readOnly
                rows={2}
                disabled
              />
            )}
            {canUpdateStart && (
              <Button
                shape="square"
                size="compact"
                kind={isSelectingStart ? 'secondary' : 'tertiary'}
                onClick={() => toggleSelectPoint(JOURNEY_START_POINT)}
              >
                <MaterialIcon
                  size="mini"
                  name="my_location"
                />
              </Button>
            )}
          </FlexRow>
          {isSelectingStart && (
            <ParagraphXSmall
              margin={0}
              color={theme.colors.contentSecondary}
            >
              Busca o selecciona un punto de partida en el mapa
            </ParagraphXSmall>
          )}
        </FlexColumn>
        <FlexColumn gap={theme.sizing.scale200}>
          <LabelSmall>Punto de llegada: *</LabelSmall>
          <FlexRow gap={theme.sizing.scale200}>
            {canUpdateEnd && (
              <MapSearchPicker
                selectedPlace={endPlace}
                onChange={onChangeEnd}
                onFocus={initEndSelectPoint}
                placeholder="Ingresa un punto de llegada"
              />
            )}
            {!canUpdateEnd && (
              <Textarea
                size="compact"
                value={editingJourney.destinationAddress || ''}
                readOnly
                rows={2}
                disabled
              />
            )}
            {canUpdateEnd && (
              <Button
                shape="square"
                size="compact"
                kind={isSelectingEnd ? 'secondary' : 'tertiary'}
                onClick={() => toggleSelectPoint(JOURNEY_END_POINT)}
                disabled={!canUpdateEnd}
              >
                <MaterialIcon
                  size="mini"
                  name="my_location"
                />
              </Button>
            )}
          </FlexRow>
          {journeySelectPoint === JOURNEY_END_POINT && (
            <ParagraphXSmall
              margin={0}
              color={theme.colors.contentSecondary}
            >
              Busca o selecciona un punto de llegada en el mapa
            </ParagraphXSmall>
          )}
        </FlexColumn>
        <FlexColumn gap={theme.sizing.scale200}>
          <StyledDateTimeRangePicker
            dates={[
              editingJourney.startDate ? new Date(editingJourney.startDate) : undefined,
              editingJourney.endDate ? new Date(editingJourney.endDate) : undefined,
            ]}
            onDatesChange={(dates) => {
              if (dates instanceof Array) {
                if (dates.length > 1 && dates[0] && dates[1]) {
                  onInputChange(dates[0].getTime(), 'startDate');
                  onInputChange(dates[1].getTime(), 'endDate');
                } else if (dates[0]) {
                  onInputChange(dates[0].getTime(), 'startDate');
                  onInputChange(null, 'endDate');
                }
              }
            }}
            onError={setDatesError}
            startLabel="Fecha y hora de salida: *"
            endLabel="Fecha y hora de llegada: *"
            startDisabled={isActive || isFinished}
            endDisabled={isFinished}
            minEndDate={minEndDate}
            minStartDate={roundToClosestIntervalTime()}
          />
          {(datesError || (!validEndDate && (canUpdateStart || canUpdateEnd))) && (
            <ParagraphXSmall
              margin={0}
              color={theme.colors.negative}
            >
              {datesError ? datesError : !validEndDate && invalidEndDateError}
            </ParagraphXSmall>
          )}
        </FlexColumn>
        {travelInfo && (
          <StyledBanner
            children={
              <FlexRow
                gap={theme.sizing.scale200}
                classNames={classes.centeredStart}
              >
                <MaterialIcon name="info" />
                <LabelSmall>
                  Para este viaje de {travelInfo.distance.text} se estima una duración de {travelInfo.duration.text}.
                </LabelSmall>
              </FlexRow>
            }
            full
            hierarchy="high"
            kind="warning"
          />
        )}
      </FlexColumn>
      <FlexRow
        classNames={`${css({ alignItems: 'center', justifyContent: isNewJourney ? 'flex-end' : 'space-between' })}`}
      >
        {!isNewJourney && (
          <StyledButton
            kind="tertiary"
            onClick={openModal}
          >
            Eliminar
          </StyledButton>
        )}
        <FlexRow gap={theme.sizing.scale300}>
          {!isFinished && (
            <>
              <StyledButton
                kind="tertiary"
                onClick={resetSelectedJourneyId}
              >
                Cancelar
              </StyledButton>
              <StyledButton
                onClick={onSaveJourney}
                isLoading={loading}
                disabled={!canSubmit || !hasUpdates}
              >
                Guardar
              </StyledButton>
            </>
          )}
          {isFinished && <StyledButton onClick={resetSelectedJourneyId}>Cerrar</StyledButton>}
        </FlexRow>
      </FlexRow>
      <ConfirmModal
        isOpen={showModal}
        onCancel={closeModal}
        onConfirm={onDeleteJourney}
        confirmText="Eliminar"
        description={<ParagraphMedium>¿Desea eliminar el trayecto "{journey?._id}"?</ParagraphMedium>}
      />
    </FloatingCardContent>
  );
}
